import { demoClients } from '../data/clients';
import {
  buildUploadInvoice,
  counterpartyPool,
  demoInvoices,
  type CurrencyRepresentation,
  type DemoInvoice,
  type ExtractedField,
  type ReprLineItem,
} from '../data/invoices';
import { error, route, type DemoRoute } from '../router';

// Mutable module-level fixture store (page reload resets — that's OK).
const invoices: DemoInvoice[] = [...demoInvoices];

// Rates to EUR for on-the-fly currency conversion (mirrors misc.ts).
const RATE_TO_EUR: Record<string, number> = { USD: 0.9214, GBP: 1.1682, BGN: 0.51129 };

function find(id: string): DemoInvoice | undefined {
  return invoices.find((inv) => inv.id === id);
}

// Advance a simulated upload from "processing" once its timer elapses.
function resolveStatus(inv: DemoInvoice): DemoInvoice {
  if (inv._processingUntil && Date.now() >= inv._processingUntil) {
    inv.status = 'review_required';
    delete inv._processingUntil;
  }
  return inv;
}

function clientObject(clientId: string) {
  const c = demoClients.find((x) => x.id === clientId);
  if (!c) return null;
  return { id: c.id, name: c.name, status: c.status, vat_code: c.vat_code, mssql_client_id: c.mssql_client_id };
}

function listItem(inv: DemoInvoice) {
  resolveStatus(inv);
  return {
    id: inv.id,
    original_filename: inv.original_filename,
    file_path: inv.file_path,
    file_size: inv.file_size,
    mime_type: inv.mime_type,
    status: inv.status,
    overall_confidence: inv.overall_confidence,
    invoice_type: inv.invoice_type,
    invoice_date: inv.invoice_date,
    uploaded_at: inv.uploaded_at,
    processed_at: inv.processed_at,
    error_message: inv.error_message,
    mirage_invoice_number: inv.mirage_invoice_number,
    client: clientObject(inv.clientId),
  };
}

function computeRepr(inv: DemoInvoice, code: string): CurrencyRepresentation {
  const rate = RATE_TO_EUR[code] ?? 1;
  const conv = (v: string | null): string | null => {
    if (v == null || v === '') return v;
    const n = Number(v);
    if (!Number.isFinite(n)) return v;
    return (n / rate).toFixed(2);
  };
  const eur = inv.representation;
  return {
    ...eur,
    id: `${inv.id}-repr-${code.toLowerCase()}`,
    currency_code: code,
    source: 'calculated',
    is_primary: false,
    subtotal: conv(eur.subtotal),
    tax_amount: conv(eur.tax_amount),
    total_amount: conv(eur.total_amount) ?? eur.total_amount,
    line_items: (eur.line_items ?? []).map((li) => ({
      ...li,
      unit_price: conv(li.unit_price),
      line_total: conv(li.line_total),
      line_tax: conv(li.line_tax),
    })),
    exchange_rate: rate.toString(),
    calculated_from_currency: 'EUR',
  };
}

function representations(inv: DemoInvoice): CurrencyRepresentation[] {
  const reprs = [inv.representation];
  if (inv.active_currency_code && inv.active_currency_code !== 'EUR') {
    reprs.push(computeRepr(inv, inv.active_currency_code));
  }
  return reprs;
}

function counterpartyFor(inv: DemoInvoice) {
  if (inv.mirage_counterparty_id == null) return null;
  return counterpartyPool.find((c) => c.id === inv.mirage_counterparty_id) ?? null;
}

function detail(inv: DemoInvoice) {
  resolveStatus(inv);
  return {
    invoice: {
      id: inv.id,
      original_filename: inv.original_filename,
      file_path: inv.file_path,
      status: inv.status,
      mirage_invoice_number: inv.mirage_invoice_number,
      overall_confidence: inv.overall_confidence,
      processed_at: inv.processed_at,
      mime_type: inv.mime_type,
      invoice_type: inv.invoice_type,
      mirage_counterparty_id: inv.mirage_counterparty_id,
      counterparty: counterpartyFor(inv),
      client: clientObject(inv.clientId),
      active_currency_code: inv.active_currency_code,
      additional_metadata: inv.additional_metadata,
    },
    extracted_data: inv.extractedData,
    currency_rate_info: inv.currency_rate_info,
    currency_representations: representations(inv),
  };
}

// ── List filtering ──────────────────────────────────────────────────────────
function matchesFilters(inv: DemoInvoice, q: URLSearchParams, opts: { ignoreStatus?: boolean } = {}): boolean {
  const status = q.get('status');
  const clientId = q.get('client_id');
  const invoiceType = q.get('invoice_type');
  const search = (q.get('search') ?? '').toLowerCase().trim();
  const uploadedFrom = q.get('uploaded_from');
  const uploadedTo = q.get('uploaded_to');
  const invDateFrom = q.get('invoice_date_from');
  const invDateTo = q.get('invoice_date_to');

  if (!opts.ignoreStatus && status && status !== 'all' && inv.status !== status) return false;
  if (clientId && inv.clientId !== clientId) return false;
  if (invoiceType && inv.invoice_type !== invoiceType) return false;
  if (uploadedFrom && inv.uploaded_at.slice(0, 10) < uploadedFrom) return false;
  if (uploadedTo && inv.uploaded_at.slice(0, 10) > uploadedTo) return false;
  if (invDateFrom && inv.invoice_date < invDateFrom) return false;
  if (invDateTo && inv.invoice_date > invDateTo) return false;
  if (search) {
    const vendor = inv.extractedData.find((f) => f.field_name === 'vendor_name')?.field_value ?? '';
    const haystack = `${inv.original_filename} ${inv.invoice_number} ${vendor}`.toLowerCase();
    if (!haystack.includes(search)) return false;
  }
  return true;
}

function listInvoices(q: URLSearchParams) {
  invoices.forEach(resolveStatus);
  const page = Math.max(1, Number(q.get('page') ?? 1));
  const limit = Math.max(1, Number(q.get('limit') ?? 50));

  const filtered = invoices.filter((inv) => matchesFilters(inv, q));
  filtered.sort((a, b) => (a.uploaded_at < b.uploaded_at ? 1 : -1));

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const start = (page - 1) * limit;
  const pageItems = filtered.slice(start, start + limit);

  const pendingReviewCount = invoices.filter(
    (inv) => inv.status === 'review_required' && matchesFilters(inv, q, { ignoreStatus: true })
  ).length;

  return {
    invoices: pageItems.map(listItem),
    total,
    total_pages: totalPages,
    page,
    limit,
    pending_review_count: pendingReviewCount,
  };
}

// ── Line-item rebuild (merge / create / delete) ─────────────────────────────
interface SerializedLineItem {
  source_group_id?: string;
  description: string;
  quantity: string;
  unit_price: string;
  unit?: string;
  line_total: string;
  vat_rate?: string;
  discount_percent?: string;
  discount_amount?: string;
  merged_from?: string[];
}

function num(v: string | null | undefined): number {
  const n = Number((v ?? '').toString().replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

function rebuildLineItems(inv: DemoInvoice, items: SerializedLineItem[]) {
  const nonLineFields = inv.extractedData.filter((f) => !f.field_name.startsWith('line_item_'));
  const newLineFields: ExtractedField[] = [];
  const reprItems: ReprLineItem[] = [];
  const existingGroups = new Set<string>();

  let netSum = 0;
  let taxSum = 0;

  items.forEach((item, idx) => {
    const group = item.source_group_id || `custom-${idx}`;
    existingGroups.add(group);

    newLineFields.push({
      id: `${inv.id}::li::${group}::line_item_description`,
      field_name: 'line_item_description',
      field_value: item.description,
      confidence_score: 0.95,
      is_validated: false,
      is_corrected: true,
      original_value: null,
      page_number: 1,
      line_item_group: group,
    });
    if (item.unit) {
      newLineFields.push({
        id: `${inv.id}::li::${group}::line_item_unit_of_measure`,
        field_name: 'line_item_unit_of_measure',
        field_value: item.unit,
        confidence_score: 0.95,
        is_validated: false,
        is_corrected: true,
        original_value: null,
        page_number: 1,
        line_item_group: group,
      });
    }

    const qty = num(item.quantity) || 1;
    const price = num(item.unit_price);
    const net = qty * price;
    const vat = num(item.vat_rate);
    const tax = (net * vat) / 100;
    netSum += net;
    taxSum += tax;

    reprItems.push({
      line_item_group: group,
      quantity: item.quantity || '1',
      unit_price: item.unit_price || null,
      line_total: item.line_total || null,
      line_tax: tax.toFixed(2),
      vat_rate: item.vat_rate ?? '20',
      discount_percent: item.discount_percent ?? null,
      discount_amount: item.discount_amount ?? null,
    });
  });

  inv.extractedData = [...nonLineFields, ...newLineFields];
  inv.representation = {
    ...inv.representation,
    subtotal: netSum.toFixed(2),
    tax_amount: taxSum.toFixed(2),
    total_amount: (netSum + taxSum).toFixed(2),
    line_items: reprItems,
    updated_at: new Date().toISOString(),
  };

  // Prune ERP metadata for groups that no longer exist.
  const erp = inv.additional_metadata.erp as { line_items?: Array<{ line_item_group: string }> } | undefined;
  if (erp?.line_items) {
    erp.line_items = erp.line_items.filter((li) => existingGroups.has(li.line_item_group));
  }

  return {
    success: true,
    extracted_data: inv.extractedData,
    representation: inv.representation,
    additional_metadata: inv.additional_metadata,
  };
}

// ── Small binary responses ──────────────────────────────────────────────────
function xmlResponse(inv: DemoInvoice): Response {
  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<Invoice>
  <Number>${inv.mirage_invoice_number ?? inv.invoice_number}</Number>
  <Date>${inv.invoice_date}</Date>
  <Supplier>${inv.extractedData.find((f) => f.field_name === 'vendor_name')?.field_value ?? ''}</Supplier>
  <Subtotal>${inv.representation.subtotal}</Subtotal>
  <Tax>${inv.representation.tax_amount}</Tax>
  <Total>${inv.representation.total_amount}</Total>
  <Currency>EUR</Currency>
</Invoice>`;
  return new Response(xml, {
    status: 200,
    headers: {
      'Content-Type': 'application/xml',
      'Content-Disposition': `attachment; filename="invoice-${inv.invoice_number}.xml"`,
    },
  });
}

function blobResponse(bytes: string, contentType: string, filename: string): Response {
  return new Response(new Blob([bytes], { type: contentType }), {
    status: 200,
    headers: {
      'Content-Type': contentType,
      'Content-Disposition': `attachment; filename="${filename}"`,
    },
  });
}

export const invoiceHandlers: DemoRoute[] = [
  // List / dashboard
  route('GET', '/invoices/', ({ query }) => listInvoices(query)),

  // Upload simulation
  route('POST', '/invoices/upload', () => {
    const inv = buildUploadInvoice();
    invoices.unshift(inv);
    return { invoice: { id: inv.id, original_filename: inv.original_filename }, duplicate: false };
  }),

  // Bulk reprocess (must precede /invoices/:id/*)
  route('POST', '/invoices/reprocess/bulk', ({ body }) => {
    const ids = ((body as { invoice_ids?: string[] })?.invoice_ids ?? []).filter(Boolean);
    const results = ids.map((id) => {
      const inv = find(id);
      if (inv) {
        inv.status = 'processing';
        inv._processingUntil = Date.now() + 7000;
        return { invoice_id: id, status: 'queued', job_id: `job-${id}` };
      }
      return { invoice_id: id, status: 'failed', reason: 'not found' };
    });
    const succeeded = results.filter((r) => r.status === 'queued').length;
    return { total: ids.length, succeeded, failed: ids.length - succeeded, results };
  }),

  // Batch export (XML) — returns a download_url the FE then GETs
  route('POST', '/invoices/batch-export', ({ body }) => {
    const ids = (body as { invoice_ids?: string[] })?.invoice_ids ?? [];
    const token = `${Date.now()}`;
    return {
      download_url: `/api/v1/invoices/batch-export/download/${token}`,
      filename: `batch_export_${ids.length}.zip`,
    };
  }),
  route('GET', '/invoices/batch-export/download/:token', () =>
    blobResponse('PK demo batch export (zip stub)', 'application/zip', 'batch_export.zip')
  ),

  // Batch export (Excel) — returns the blob directly
  route('POST', '/invoices/batch-export-excel', () =>
    blobResponse(
      'demo,excel,export\nБрой,Сума,Валута',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'invoices_export.xlsx'
    )
  ),

  // Client identifications confirm (2-segment; keep before others is fine)
  route('POST', '/client-identifications/confirm', () => ({ created: 1, updated: 0 })),

  // Per-invoice detail
  route('GET', '/invoices/:id', ({ params }) => {
    const inv = find(params.id);
    return inv ? detail(inv) : error(404, 'Invoice not found');
  }),

  route('GET', '/invoices/:id/download', ({ params }) => {
    const inv = find(params.id);
    if (!inv) return error(404, 'Invoice not found');
    return { download_url: inv.pdfUrl, filename: inv.original_filename };
  }),

  route('GET', '/invoices/:id/export', ({ params }) => {
    const inv = find(params.id);
    if (!inv) return error(404, 'Invoice not found');
    return xmlResponse(inv);
  }),

  route('GET', '/invoices/:id/counterparties', ({ params }) => {
    const inv = find(params.id);
    if (!inv) return error(404, 'Invoice not found');
    return { counterparties: counterpartyPool };
  }),

  // Status
  route('PUT', '/invoices/:id/status', ({ params, body }) => {
    const inv = find(params.id);
    if (!inv) return error(404, 'Invoice not found');
    const next = (body as { status?: string })?.status;
    if (next) inv.status = next;
    return { id: inv.id, status: inv.status };
  }),

  route('POST', '/invoices/:id/return-to-review', ({ params }) => {
    const inv = find(params.id);
    if (!inv) return error(404, 'Invoice not found');
    inv.status = 'review_required';
    return { message: 'Invoice returned to review', status: inv.status };
  }),

  route('POST', '/invoices/:id/reprocess', ({ params }) => {
    const inv = find(params.id);
    if (!inv) return error(404, 'Invoice not found');
    inv.status = 'processing';
    inv._processingUntil = Date.now() + 7000;
    return {
      message: 'Reprocessing started',
      invoice_id: inv.id,
      job_id: `job-${inv.id}`,
      status: 'processing',
      children_deleted: 0,
    };
  }),

  route('DELETE', '/invoices/:id', ({ params }) => {
    const idx = invoices.findIndex((inv) => inv.id === params.id);
    if (idx === -1) return error(404, 'Invoice not found');
    invoices.splice(idx, 1);
    return { success: true };
  }),

  // Field update
  route('PUT', '/invoices/:id/fields/:fieldId', ({ params, body }) => {
    const inv = find(params.id);
    if (!inv) return error(404, 'Invoice not found');
    const field = inv.extractedData.find((f) => f.id === params.fieldId);
    if (!field) return error(404, 'Field not found');
    const b = body as { field_value?: string; is_corrected?: boolean };
    if (b.field_value !== undefined) {
      if (field.original_value == null && field.field_value !== b.field_value) {
        field.original_value = field.field_value;
      }
      field.field_value = b.field_value;
    }
    if (b.is_corrected !== undefined) field.is_corrected = b.is_corrected;
    else field.is_corrected = true;
    return { field, currency_rate_info: inv.currency_rate_info };
  }),

  // VIES VAT validation
  route('POST', '/invoices/:id/fields/:fieldId/validate-vat', ({ params }) => {
    const inv = find(params.id);
    if (!inv) return error(404, 'Invoice not found');
    const field = inv.extractedData.find((f) => f.id === params.fieldId);
    if (!field) return error(404, 'Field not found');
    const traderName = inv.extractedData.find((f) => f.field_name === 'vendor_name')?.field_value ?? '';
    const vies = {
      status: 'valid' as const,
      validated_at: new Date().toISOString(),
      tax_id_checked: field.field_value,
      country_code: field.field_value.slice(0, 2),
      vat_number: field.field_value.slice(2),
      trader_name: traderName,
      trader_address: inv.extractedData.find((f) => f.field_name === 'vendor_address')?.field_value ?? '',
      source: 'VIES',
    };
    field.extra_metadata = { ...(field.extra_metadata ?? {}), vies_validation: vies };
    return { vies_validation: vies, field: { id: field.id, extra_metadata: field.extra_metadata } };
  }),

  // Metadata (invoice-level ERP)
  route('PUT', '/invoices/:id/metadata', ({ params, body }) => {
    const inv = find(params.id);
    if (!inv) return error(404, 'Invoice not found');
    const erpBody = (body as { erp?: Record<string, unknown> })?.erp ?? {};
    inv.additional_metadata.erp = { ...(inv.additional_metadata.erp as object), ...erpBody };
    return { success: true, message: 'Metadata updated', rule_generation_job_id: null };
  }),

  // Line items
  route('PUT', '/invoices/:id/line-items', ({ params, body }) => {
    const inv = find(params.id);
    if (!inv) return error(404, 'Invoice not found');
    const items = (body as { line_items?: SerializedLineItem[] })?.line_items ?? [];
    return rebuildLineItems(inv, items);
  }),

  route('POST', '/invoices/:id/line-items/:group/restore', ({ params }) => {
    const inv = find(params.id);
    if (!inv) return error(404, 'Invoice not found');
    return {
      success: true,
      extracted_data: inv.extractedData,
      representation: inv.representation,
      additional_metadata: inv.additional_metadata,
    };
  }),

  route('PUT', '/invoices/:id/line-items/:group/erp-metadata', ({ params, body }) => {
    const inv = find(params.id);
    if (!inv) return error(404, 'Invoice not found');
    const erp = inv.additional_metadata.erp as { line_items?: Array<Record<string, unknown>> };
    if (!Array.isArray(erp.line_items)) erp.line_items = [];
    const meta = { line_item_group: params.group, ...(body as Record<string, unknown>) };
    const existingIdx = erp.line_items.findIndex((li) => li.line_item_group === params.group);
    if (existingIdx >= 0) erp.line_items[existingIdx] = meta;
    else erp.line_items.push(meta);
    // Occasionally surface a learning job so the "rule saved" toast fires.
    const jobId = Math.random() < 0.5 ? `rule-job-${Date.now()}` : null;
    return { success: true, message: 'ERP metadata updated', rule_generation_job_id: jobId };
  }),

  // Representation line-item field patch
  route('PATCH', '/invoices/:id/representation-line-items/:group', ({ params, body }) => {
    const inv = find(params.id);
    if (!inv) return error(404, 'Invoice not found');
    const { field, value } = (body as { field?: string; value?: string }) ?? {};
    const li = inv.representation.line_items?.find((x) => x.line_item_group === params.group);
    if (li && field && field in li) {
      (li as unknown as Record<string, string | null>)[field] = value ?? null;
    }
    inv.representation = { ...inv.representation, updated_at: new Date().toISOString() };
    return { success: true, representation: inv.representation };
  }),

  // Client assignment
  route('PUT', '/invoices/:id/client', ({ params, body }) => {
    const inv = find(params.id);
    if (!inv) return error(404, 'Invoice not found');
    const clientId = (body as { client_id?: string | null })?.client_id ?? null;
    inv.clientId = clientId ?? inv.clientId;
    return { message: 'Client updated', client_id: clientId };
  }),

  // Counterparty assignment
  route('PUT', '/invoices/:id/counterparty', ({ params, body }) => {
    const inv = find(params.id);
    if (!inv) return error(404, 'Invoice not found');
    const cpId = (body as { counterparty_id?: number | null })?.counterparty_id ?? null;
    inv.mirage_counterparty_id = cpId;
    const cp = cpId != null ? counterpartyPool.find((c) => c.id === cpId) ?? null : null;
    return { message: 'Counterparty updated', counterparty_id: cpId, counterparty: cp };
  }),

  // Currency rate override
  route('PATCH', '/invoices/:id/currency-rate-override', ({ params, query }) => {
    const inv = find(params.id);
    if (!inv) return error(404, 'Invoice not found');
    const rateValue = query.get('rate_value') ?? '1';
    inv.currency_rate_info = {
      ...inv.currency_rate_info,
      override_rate: rateValue,
      effective_rate: rateValue,
      is_overridden: true,
    };
    return { message: 'Rate overridden', override_rate: rateValue, original_shared_rate: '1.00000000' };
  }),
  route('DELETE', '/invoices/:id/currency-rate-override', ({ params }) => {
    const inv = find(params.id);
    if (!inv) return error(404, 'Invoice not found');
    inv.currency_rate_info = { ...inv.currency_rate_info, override_rate: null, is_overridden: false };
    return { message: 'Override cleared' };
  }),

  // Active currency switch
  route('PATCH', '/invoices/:id/active-currency', ({ params, query }) => {
    const inv = find(params.id);
    if (!inv) return error(404, 'Invoice not found');
    const code = (query.get('currency_code') ?? 'EUR').toUpperCase();
    inv.active_currency_code = code;
    return { active_currency_code: code, currency_representations: representations(inv) };
  }),
];
